import React, { useState, useCallback, useRef } from 'react';
import AsyncSelect from 'react-select/async';
import debounce from 'lodash/debounce';
import { LightText, SmallFont } from './Style';
import useToggle from '../hooks/useToggle';
import api from '../api';

function SearchIcon() {
  return (
    <div style={{ padding: '0 8px', display: 'flex', alignItems: 'center' }}>
      <svg
        width="16"
        height="16"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#999"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="11" cy="11" r="8" />
        <line x1="21" y1="21" x2="16.65" y2="16.65" />
      </svg>
    </div>
  );
}

// `defaultType` ('songs' | 'albums') locks the search to one media type and
// hides the toggles. Used by the list composer, where every item in a list must
// match the list's own kind, so letting the user mix the two would only produce
// items the server rejects.
export default function Search({ setMediaItem, autoFocus, defaultType = null }) {
  const [inputValue, setInputValue] = useState('');
  const [selectedValue, setSelectedValue] = useState(null);
  const [includesAlbums, toggleAlbumSearch] = useToggle(
    defaultType ? defaultType === 'albums' : true
  );
  const [includesSongs, toggleSongSearch] = useToggle(defaultType ? defaultType === 'songs' : true);

  // handle input change event — allow all actions including paste ("set-value")
  const handleInputChange = (value, { action }) => {
    if (action === 'input-change' || action === 'set-value') {
      setInputValue(value);
    }
  };

  // handle selection (null when cleared)
  const handleChange = (value) => {
    setSelectedValue(value);
    setMediaItem(value);
  };

  function getAsyncOptions(props) {
    var types = '';

    if (props.includesAlbums) {
      types = types + 'albums,';
    }

    if (props.includesSongs) {
      types = types + 'songs';
    }

    return api
      .get('/api/search', {
        params: { term: props.input, limit: 20, types: types },
      })
      .then((res) => res.data)
      .catch((error) => {
        console.error('There was an error!', error);
      });
  }

  // Use refs so the debounced function always sees current toggle values
  // without needing to be recreated (which would cancel in-flight searches)
  var includesAlbumsRef = useRef(includesAlbums);
  includesAlbumsRef.current = includesAlbums;
  var includesSongsRef = useRef(includesSongs);
  includesSongsRef.current = includesSongs;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const loadOptions = useCallback(
    debounce((input, callback) => {
      getAsyncOptions({
        input: input,
        includesAlbums: includesAlbumsRef.current,
        includesSongs: includesSongsRef.current,
      }).then((options) => callback(options));
    }, 500),
    []
  );

  function getOptionLabels(e) {
    var title = e.song_title || e.album_title;
    var typeLabel = e.song_title ? 'Song' : 'Album';
    return title + ' - ' + e.artist + ' (' + typeLabel + ')';
  }

  const getOptionValues = (e) => e;

  function Checkbox(props) {
    return <input type="checkbox" {...props} />;
  }

  return (
    <div>
      <AsyncSelect
        autoFocus={autoFocus}
        inputValue={inputValue}
        value={selectedValue}
        isClearable
        getOptionLabel={getOptionLabels}
        getOptionValue={getOptionValues}
        loadOptions={loadOptions}
        onInputChange={handleInputChange}
        onChange={handleChange}
        placeholder="Search..."
        components={{ DropdownIndicator: SearchIcon, IndicatorSeparator: null }}
      />

      {!defaultType && (
        <SmallFont>
          <LightText Tag="albums" style={{ marginLeft: '0em' }}>
            <Checkbox checked={includesAlbums} onChange={toggleAlbumSearch} />
            Search albums
          </LightText>

          <LightText Tag="songs" style={{ marginLeft: '1em' }}>
            <Checkbox checked={includesSongs} onChange={toggleSongSearch} />
            Search songs
          </LightText>
        </SmallFont>
      )}
    </div>
  );
}
